import { NextImage } from '@zurich-framework/nextapp/components';
import { useTranslation, useValidationError } from '@zurich/common/hooks';
import { appsVSGADataNames } from '@zurich/test/testdatanames';
import { Button, Stack, Typography } from '@zurich/ui/atoms';
import { styled } from '@zurich/ui/legacy/utils';
import { CameraButton } from '@zurich/ui/molecules';
import { DamageFields } from '~/components/Steps/DamageFields';
import { useDamageUpload } from '~/hooks';
import type { AutoArea } from '~/lib/types';
import { damageDocumentValidationSelectorFamily } from '~/state';

type DocumentDamageOverviewProps = {
  id: string;
  step: AutoArea;
  handleConfirmation: () => void;
};
export function DocumentDamageOverview({
  id,
  step,
  handleConfirmation,
}: DocumentDamageOverviewProps) {
  const { t } = useTranslation();
  const { blobURL, loading, error, ...damageProps } = useDamageUpload(id, step);

  const { stepValidationActive, validationError } = useValidationError(
    damageDocumentValidationSelectorFamily(id)
  );

  return (
    <StackStyled rowGap={3} maxWidth="800px" width="100%">
      <Typography variant="body2 regular" color="darkBlue">
        {t('vsga:damagePhoto.bodyDetailPhoto')}
      </Typography>
      <Typography variant="body2 regular" color="darkBlue">
        {t('vsga:damagePhoto.bodyDetailPhotoDescription')}
      </Typography>
      <Stack flexDirection="row" justifyContent="center">
        {!blobURL && (
          <NextImageStyled
            src="/images/image_placeholder.svg"
            alt="image placeholder"
            width={310}
            height={240}
          />
        )}
      </Stack>
      <ImageContainerStyled>
        <CameraButton
          {...damageProps}
          loading={loading}
          loadingTitle={t('photoLoadingTitle')}
          imageURL={blobURL}
          text={t('vsga:general.nextButtonPhoto')}
          capture="environment"
          errorMessage={t('vsga:general.unsupportedFileType', {
            datatype: 'ein Bild',
          })}
          dataName={appsVSGADataNames.damageDocumentation.stepDamageInput}
        />
        {error && (
          <Stack alignItems="center" justifyContent="center">
            <Typography variant="body1 regular" color="error">
              {t(`vsga:errors.${error}`)}
            </Typography>
          </Stack>
        )}

        {stepValidationActive && validationError && (
          <Typography color="error" variant="body1 regular">
            {t(`vsga:general.photoUploadValidation.${validationError}`)}
          </Typography>
        )}
      </ImageContainerStyled>
      <DamageFields id={id} step={step} />
      <Stack flexDirection="row" justifyContent="center">
        <Button onClick={handleConfirmation}>
          {t('common:button_label.next')}
        </Button>
      </Stack>
    </StackStyled>
  );
}

const StackStyled = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(0, 2, 3),
}));

const ImageContainerStyled = styled(Stack)(({ theme }) => ({
  rowGap: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

const NextImageStyled = styled(NextImage)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
}));
